import React, { ChangeEvent } from 'react';
import { Autocomplete, Box, FormControl, SelectChangeEvent, TextField } from '@mui/material';

import { Organization } from '@paragon/contract-library';
import { notNullish } from '@paragon/utilities';

import { useAppSelector } from '../hooks';

interface UserBase {
  readonly organization: string | null;
  readonly email: string;
  readonly firstName: string;
  readonly lastName: string;
  readonly phone: string;
  readonly location: string;
  readonly password?: string;
}

interface UserPropertyDefinitionBase {
  readonly key: UserPropertyKey;
  readonly label: string;
  readonly required: boolean;
  readonly inputType: 'text' | 'select';
  readonly disabled?: boolean;
}

interface SelectPropertyDefinition extends UserPropertyDefinitionBase {
  readonly inputType: 'select';
  readonly options: readonly string[];
  readonly selectedOption: string | null;
  readonly optionsLoading: boolean;
}

interface TextPropertyDefinition extends UserPropertyDefinitionBase {
  readonly inputType: 'text';
}

type UserPropertyDefinition = SelectPropertyDefinition | TextPropertyDefinition;

type UserPropertyKey = 'organization' | 'email' | 'firstName' | 'lastName' | 'phone' | 'location' | 'password';

type Validate = {
  readonly [K in UserPropertyKey]: (value: string | null | undefined) => boolean | undefined;
};

interface Props<User extends UserBase> {
  readonly user: User;
  readonly organizations: readonly Organization[];
  readonly isCreating?: boolean;
  readonly onChange: (user: User) => void;
  readonly validate: Validate;
  readonly showErrors: boolean;
}

export function UserProperties<User extends UserBase>({
  user,
  organizations,
  isCreating,
  onChange,
  validate,
  showErrors,
}: Props<User>) {
  const editingAsRootUser = useAppSelector((state) => state.logInData.loggedInUser!.permissions.includes('root'));

  const onPropertyChanged = (
    key: UserPropertyKey,
    event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement> | SelectChangeEvent | string | null,
  ) => {
    if (typeof onChange === 'function') {
      let value = event === null ? null : typeof event === 'string' ? event : event.target.value;
      if (value === '') value = null;
      const updatedUserProperties = { ...user, [key]: value };
      onChange(updatedUserProperties);
      // special case to sync location on organization change
      if (key === 'organization') {
        const newOrganization = organizations.find((org) => org.name === value);
        onChange({
          ...updatedUserProperties,
          location: newOrganization && newOrganization.locations.length > 0 ? newOrganization.locations[0] : null,
        });
      }
    }
  };

  function renderProperty(definition: UserPropertyDefinition) {
    let control;

    if (definition.inputType === 'select') {
      control = renderSelectProperty(definition);
    } else {
      control = renderTextboxProperty(definition);
    }

    return (
      <FormControl
        variant="standard"
        key={definition.key}
        sx={{ m: 1, ml: 0, width: '100%' }}
        disabled={definition.disabled}
      >
        {control}
      </FormControl>
    );
  }

  function renderTextboxProperty(definition: TextPropertyDefinition) {
    const { key } = definition;
    const value = user[key] || '';

    return (
      <TextField
        variant="standard"
        required={definition.required}
        error={showErrors && validate[key](user[key])}
        key={key}
        type={definition.inputType}
        id={value}
        label={definition.label}
        disabled={definition.disabled}
        sx={{ mr: 1, width: '100%' }}
        value={value}
        onChange={(event) => {
          onPropertyChanged(key, event);
        }}
        margin="normal"
      />
    );
  }

  function renderSelectProperty(definition: SelectPropertyDefinition) {
    const invalidOptionSelected =
      !definition.optionsLoading &&
      definition.selectedOption != null &&
      !definition.options.includes(definition.selectedOption);
    return (
      <Autocomplete
        key={definition.key}
        options={definition.options}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="standard"
            label={definition.label}
            required={definition.required}
            error={invalidOptionSelected || (showErrors && validate[definition.key](definition.selectedOption))}
          />
        )}
        renderOption={(props, option) => (
          <li {...props} key={option}>
            {option}
          </li>
        )}
        value={definition.selectedOption ?? ''}
        onChange={(_, selectedOption) => {
          onPropertyChanged(definition.key, selectedOption);
        }}
        disabled={definition.disabled}
        sx={{ width: '100%' }}
      />
    );
  }

  const locations = organizations.find((obj) => obj.name === user.organization)?.locations ?? [];

  const userPropertyDefinitions: readonly UserPropertyDefinition[] = [
    {
      key: 'organization' as const,
      label: 'Organization',
      required: true,
      inputType: 'select' as const,
      options: organizations.map((organization) => organization.name),
      selectedOption: user.organization,
      disabled: !editingAsRootUser,
      optionsLoading: organizations.length === 0,
    },
    {
      key: 'email' as const,
      label: 'Email',
      required: true,
      inputType: 'text' as const,
      disabled: !isCreating,
    },
    { key: 'firstName' as const, label: 'First Name', required: true, inputType: 'text' as const },
    { key: 'lastName' as const, label: 'Last Name', required: true, inputType: 'text' as const },
    { key: 'phone' as const, label: 'Phone #', required: true, inputType: 'text' as const },
    {
      key: 'location' as const,
      label: 'Location',
      required: true,
      inputType: 'select' as const,
      options: locations,
      selectedOption: user.location,
      optionsLoading: organizations.length === 0,
    },
    isCreating ? { key: 'password' as const, label: 'Password', required: false, inputType: 'text' as const } : null,
  ].filter(notNullish);

  return (
    <Box component="form" sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {userPropertyDefinitions.map((userPropertyDefinition) => renderProperty(userPropertyDefinition))}
    </Box>
  );
}
